import { useMemo } from 'react';

import DataTable from '@shared/ui/data-table';
import type { Column } from '@shared/ui/data-table';
import { PushBadge } from '@entities/marketing-push/ui';
import { formatPushDate, formatPushNumber } from '@entities/marketing-push/lib';
import { CAMPAIGN_STATUS_LABEL } from '../../../types/marketingPushInterfaces';
import type { PushCampaignListItem } from '../../../types/marketingPushInterfaces';

interface Props {
  rows: PushCampaignListItem[];
  loading: boolean;
  onRowClick: (campaign: PushCampaignListItem) => void;
  onReport: (campaign: PushCampaignListItem) => void;
  onDuplicate?: (campaign: PushCampaignListItem) => void;
  onDelete?: (campaign: PushCampaignListItem) => void;
}

export function PushCampaignsTable({
  rows,
  loading,
  onRowClick,
  onReport,
  onDuplicate,
  onDelete,
}: Props) {
  const columns: Column<PushCampaignListItem>[] = useMemo(() => {
    const base: Column<PushCampaignListItem>[] = [
      {
        key: 'title',
        header: 'Рассылка',
        render: (campaign) => (
          <div className="min-w-0">
            <p
              className="max-w-[380px] truncate font-medium text-[#2d2c2c]"
              title={campaign.title}
            >
              {campaign.title || <span className="text-gray-400">без заголовка</span>}
            </p>
            <p className="truncate text-xs text-gray-400">
              #{campaign.id} · {campaign.notification_type} · {campaign.languages.join(', ')}
            </p>
          </div>
        ),
      },
      {
        key: 'targets',
        header: 'Приложения',
        render: (campaign) => (
          <div className="flex flex-wrap gap-1">
            {campaign.targets.map((target) => (
              <PushBadge key={target}>{target}</PushBadge>
            ))}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Статус',
        render: (campaign) => (
          <div className="flex flex-col gap-1">
            <PushBadge tone={CAMPAIGN_STATUS_LABEL[campaign.status].tone}>
              {CAMPAIGN_STATUS_LABEL[campaign.status].label}
            </PushBadge>
            {campaign.is_test && <PushBadge tone="info">тест</PushBadge>}
          </div>
        ),
      },
      {
        key: 'audience',
        header: 'Получателей',
        headerClassName: 'text-right',
        className: 'text-right',
        render: (campaign) => (
          <span className="text-[#2d2c2c]">
            {formatPushNumber(campaign.locked_audience_size)}
          </span>
        ),
      },
      {
        key: 'created',
        header: 'Создана',
        render: (campaign) => (
          <div className="text-xs">
            <span className="text-[#2d2c2c]">{formatPushDate(campaign.created_at)}</span>
            {campaign.scheduled_at && (
              <span className="block text-sky-700">
                на {formatPushDate(campaign.scheduled_at)}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        headerClassName: 'w-[1%] whitespace-nowrap',
        // Клик по этой ячейке не должен открывать рассылку: здесь свои
        // кнопки, и переход поверх них раздражает.
        stopRowClick: true,
        render: (campaign) => (
          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => onReport(campaign)}
            >
              Отчёт
            </button>
            {onDuplicate && (
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => onDuplicate(campaign)}
              >
                Копировать
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="text-xs text-red-600 hover:underline"
                onClick={() => onDelete(campaign)}
              >
                Удалить
              </button>
            )}
          </div>
        ),
      },
    ];
    return base;
  }, [onDelete, onDuplicate, onReport]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      keyAccessor={(campaign) => campaign.id}
      loading={loading}
      onRowClick={onRowClick}
      emptyMessage="Рассылок пока нет"
    />
  );
}

export default PushCampaignsTable;
